'use client';

import { uiFb } from '@/lib/i18n';
import { useCalendarStore } from '@/stores/calendar-store';
import { LENS_COLORS, LEGEND_ITEMS } from '@/lib/constants';

/**
 * Persistent calendar-grid legend.
 *
 * Two layers:
 *  - Always-visible mini chips (Today / Density / Collision) — the three
 *    most distinctive signals on the grid, compressed into a 3-chip row.
 *  - <details> disclosure that expands to a full key: every event-type
 *    color, every lens color, the density scale, and selected/past states.
 *
 * Best-of-both: passing visitors learn the basics from the chips; curious
 * users drill in via the "?" without permanently sacrificing toolbar space.
 *
 * Refs: almanak-5m833 (filed from the deferred audit note in almanak-3pfa2.1.5)
 */
export function CalendarLegend() {
  const lang = useCalendarStore((s) => s.lang) || 'en';
  return (
    <details className="group inline-flex shrink-0 items-center">
      <summary
        aria-label={uiFb('ui.legend_aria', 'Calendar legend', lang)}
        className="inline-flex cursor-pointer list-none items-center gap-2 rounded-full border border-border-subtle bg-surface-card px-3 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-accent-primary hover:text-text-primary [&::-webkit-details-marker]:hidden"
      >
        {/* Today chip */}
        <span className="inline-flex items-center gap-1">
          <span
            className="inline-block h-3 w-3 rounded border border-brand/40"
            style={{ backgroundColor: 'rgba(var(--brand-rgb, 59 130 246), 0.18)' }}
          />
          <span>{uiFb('ui.nav.today', 'Today', lang)}</span>
        </span>
        {/* Density chip — gradient sample */}
        <span className="inline-flex items-center gap-1">
          <span
            className="inline-block h-3 w-6 rounded"
            style={{ background: 'linear-gradient(to right, rgba(var(--year-bar-rgb), 0.08), rgba(var(--year-bar-rgb), 0.36))' }}
            aria-hidden="true"
          />
          <span>{uiFb('ui.legend.density', 'Density', lang)}</span>
        </span>
        {/* Collision chip — split-color sample */}
        <span className="inline-flex items-center gap-1">
          <span
            className="inline-block h-3 w-3 rounded"
            style={{
              background: `linear-gradient(135deg, color-mix(in srgb, ${LENS_COLORS.country} 40%, transparent) 0% 50%, color-mix(in srgb, ${LENS_COLORS.religion} 40%, transparent) 50% 100%)`,
            }}
            aria-hidden="true"
          />
          <span>{uiFb('ui.legend.collision', 'Collision', lang)}</span>
        </span>
        {/* "?" toggle indicator */}
        <span aria-hidden="true" className="text-text-tertiary group-open:rotate-180 transition-transform">
          ▾
        </span>
      </summary>

      {/* Expanded full key */}
      <div className="absolute z-20 mt-2 max-w-xs content-card p-4 shadow-lg">
        <h3 className="font-heading text-sm font-semibold text-text-primary">
          {uiFb('ui.legend.heading', 'Calendar key', lang)}
        </h3>

        {/* Event-type colors */}
        <div className="mt-3">
          <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            {uiFb('ui.legend.event_types', 'Event types', lang)}
          </p>
          <ul className="mt-2 grid grid-cols-2 gap-1.5">
            {LEGEND_ITEMS.map((item) => (
              <li key={item.type} className="flex items-center gap-1.5 text-xs text-text-secondary">
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span>{uiFb(`ui.type.${item.type}`, item.label, lang)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Lens colors */}
        <div className="mt-3">
          <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            {uiFb('ui.legend.lenses', 'Lenses (collision colors)', lang)}
          </p>
          <ul className="mt-2 grid grid-cols-2 gap-1.5">
            {Object.entries(LENS_COLORS).map(([id, color]) => (
              <li key={id} className="flex items-center gap-1.5 text-xs text-text-secondary">
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: color }}
                />
                <span>{uiFb(`ui.lens.${id}`, capitalize(id), lang)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Density scale */}
        <div className="mt-3">
          <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            {uiFb('ui.legend.density_scale', 'Density (events per day)', lang)}
          </p>
          <div className="mt-2 flex items-center gap-1">
            {[1, 3, 5, 7].map((n) => (
              <div key={n} className="flex flex-col items-center gap-0.5">
                <span
                  aria-hidden="true"
                  className="inline-block h-4 w-6 rounded border border-border-subtle"
                  style={{ backgroundColor: `rgba(var(--year-bar-rgb), ${Math.min(0.08 + n * 0.04, 0.36)})` }}
                />
                <span className="text-[10px] text-text-tertiary">{n === 7 ? '7+' : n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </details>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
